import { useState, useMemo, useCallback, useEffect } from "react";
import { FiX } from "react-icons/fi";
import axios from "axios";
import debounce from "lodash.debounce";
import { toast } from "react-toastify";
import { url } from "../utils/config";
import Input from "../reusable_components/Input";
import Select from "../reusable_components/Select";
import postData from "../custom_functions/postData";
import patchData from "../custom_functions/patchData";
import { modalContainer } from "../utils/style";

const transmissionOptions = ["Auto", "Manual", "CVT"];
const ownershipOptions = ["Owned", "Leased", "Rented"];
const fuelTypeOptions = ["Gasoline", "Diesel", "Electric", "Hybrid", "Other"];
const drivetrainOptions = ["FWD", "RWD", "AWD", "4WD"];
const odoUnitOptions = ["mi", "km"];
const fieldLabelClass = "mb-1 block text-xs font-semibold uppercase tracking-wide text-emerald-600";
const fieldInputClass = "w-full rounded-xl border border-emerald-200 bg-white px-4 py-2.5 text-sm text-emerald-900 shadow-sm placeholder:text-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-400";

const createInitialFormState = () => ({
  make: "",
  model: "",
  year: "",
  vin: "",
  plate: "",
  transmission: "",
  ownership: "",
  warranty_expiration_date: "",
  in_service_date: "",
  plate_state_province: "",
  color: "",
  engine: "",
  tire_size_front: "",
  tire_size_rear: "",
  vehicle_number: "",
  trim: "",
  fuel_type: "",
  drivetrain: "",
  odo_unit: "",
  current_odometer: "",
  notes: "",
  group: "",
  acquisition_cost: "",
  location: "",
});

function AddFleetCar({ setShowModal, setReloadData, cars, otherData, setOtherData }) {
  const [formData, setFormData] = useState(() => createInitialFormState());
  const [missingFields, setMissingFields] = useState([]);
  const [inputErrors, setInputErrors] = useState([]);
  const [showInputError, setShowInputError] = useState(false);

  const requiredFields = ["make", "model", "year", "vin"];
  const isEditMode = Boolean(otherData?.id);

  const getMakeList = useMemo(() => {
    const carList = Array.isArray(cars) ? cars : [];
    const list = [...new Set(carList.map((car) => car.make))];
    if (formData.make && !list.includes(formData.make)) list.push(formData.make);
    return list.sort();
  }, [cars, formData.make]);

  function getModelList() {
    const safeCars = Array.isArray(cars) ? cars : [];
    const list = safeCars
      .filter((c) => c.make === formData.make)
      .map((c) => c.model);
    if (formData.model && !list.includes(formData.model)) list.push(formData.model);
    return [...new Set(list)].sort();
  }

  function getYearList() {
    const safeCars = Array.isArray(cars) ? cars : [];
    const list = safeCars
      .filter((c) => c.make === formData.make && c.model === formData.model)
      .map((c) => c.year);
    if (formData.year && !list.includes(formData.year)) list.push(formData.year);
    return [...new Set(list)].sort();
  }

  const resetForm = useCallback(() => {
    setFormData(createInitialFormState());
    setMissingFields([]);
    setShowInputError(false);
    setInputErrors([]);
    setOtherData(null);
  }, [setOtherData]);

  const handleClose = useCallback(() => {
    resetForm();
    setShowModal(false);
  }, [resetForm, setShowModal]);

  function updateFormData(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function handleVinChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    debouncedVinLookup(e.target.value.trim());
  }

  const debouncedVinLookup = useCallback(
    debounce((vin) => {
      if (vin.length >= 10) fetchVehicleInfo(vin);
    }, 600),
    []
  );

  const fetchVehicleInfo = async (vin) => {
    try {
      const reqUrl = url + `/vin_details?vin=${encodeURIComponent(vin)}`;
      const token = window.localStorage.getItem("fm_token");
      const headers = { authorization: `bearer ${token}` };
      const { data } = await axios.get(reqUrl, { headers });

      if (data.data.make && data.data.model && data.data.year) {
        setFormData((prev) => ({
          ...prev,
          make: data.data.make,
          model: data.data.model,
          year: data.data.year
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          make: "",
          model: "",
          year: ""
        }));
        toast.info("VIN details not found, please choose manually", { autoClose: 3000 });
      }

    } catch (err) {
      setFormData((prev) => ({
        ...prev,
        make: "",
        model: "",
        year: "",
      }));
    }
  };

  useEffect(() => {
    if (otherData) {
      setFormData((prev) => ({ ...createInitialFormState(), ...otherData }));
    } else {
      setFormData(createInitialFormState());
    }
  }, [otherData]);

  async function addFleetCar() {
    if (
      formData.make &&
      formData.model &&
      formData.year &&
      formData.vin &&
      !inputErrors.includes("error")
    ) {
      postData(
        `${url}/fleet_customer_car`,
        { ...formData },
        (error) => {
          toast.error(error.response.data.message, { autoClose: 3000 });
          setOtherData(null);
        },
        (data) => {
          toast.success(data.message, { autoClose: 3000 });
          setReloadData((prev) => !prev);
          setShowModal(false);
          resetForm();
        }
      );
    } else {
      let miss = [];
      requiredFields.forEach((field) => {
        if (!formData[field]) {
          miss.push(field);
        }
      });
      setMissingFields(miss);
      setShowInputError(true);
    }
  }

  async function updateFleetCar() {
    if (
      formData.make &&
      formData.model &&
      formData.year &&
      formData.vin &&
      !inputErrors.includes("error")
    ) {
      patchData(
        `${url}/fleet_customer_car/${otherData.id}`,
        { ...formData },
        (error) => {
          toast.error(error.response.data.message, { autoClose: 3000 });
          setOtherData(null);
        },
        (data) => {
          toast.success(data.message, { autoClose: 3000 });
          setReloadData((prev) => !prev);
          setShowModal(false);
          resetForm();
        }
      );
    } else {
      let miss = [];
      requiredFields.forEach((field) => {
        if (!formData[field]) {
          miss.push(field);
        }
      });
      setMissingFields(miss);
      setShowInputError(true);
    }
  }

  return (
    <div className={modalContainer}>
      <div className="relative w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-emerald-100 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-emerald-900">
              {isEditMode ? "Update Fleet Vehicle" : "Add Fleet Vehicle"}
            </h2>
            <p className="mt-1 text-sm text-emerald-500">
              {isEditMode
                ? "Modify the fields below and save your changes."
                : "Fill in the details below to register a new vehicle."}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-emerald-100 text-emerald-400 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
          <div className="space-y-5">
            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-500">
                Vehicle Overview
              </h3>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                  <label className={fieldLabelClass}>
                    VIN
                  </label>
                  <Input
                    index={3}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name="vin"
                    required={true}
                    value={formData.vin}
                    className={fieldInputClass}
                    placeHolder="VIN"
                    onChange={handleVinChange}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Make
                  </label>
                  <Select
                    index={0}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name="make"
                    type="text"
                    required={true}
                    value={formData.make}
                    className={fieldInputClass}
                    placeHolder="Make"
                    onChange={updateFormData}
                    options={getMakeList}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Model
                  </label>
                  <Select
                    index={1}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name="model"
                    type="text"
                    required={true}
                    value={formData.model}
                    className={fieldInputClass}
                    placeHolder="Model"
                    onChange={updateFormData}
                    options={getModelList()}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Year
                  </label>
                  <Select
                    index={2}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name="year"
                    type="year"
                    required={true}
                    value={formData.year}
                    className={fieldInputClass}
                    placeHolder="Year"
                    onChange={updateFormData}
                    options={getYearList()}
                  />
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-500">
                Service Details
              </h3>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                  <label className={fieldLabelClass}>
                    Transmission
                  </label>
                  <Select
                    index={12}
                    required={false}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name="transmission"
                    value={formData.transmission}
                    className={fieldInputClass}
                    placeHolder="Transmission"
                    onChange={updateFormData}
                    options={transmissionOptions}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Ownership
                  </label>
                  <Select
                    index={17}
                    name="ownership"
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    required={false}
                    value={formData.ownership}
                    className={fieldInputClass}
                    placeHolder="Ownership"
                    onChange={updateFormData}
                    options={ownershipOptions}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Warranty Expiration Date
                  </label>
                  <Input
                    index={18}
                    name="warranty_expiration_date"
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    value={formData.warranty_expiration_date || ""}
                    className={fieldInputClass}
                    onChange={updateFormData}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    In Service Date
                  </label>
                  <Input
                    index={19}
                    name="in_service_date"
                    type="date"
                    value={formData.in_service_date || ""}
                    className={fieldInputClass}
                    onChange={updateFormData}
                  />
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-500">
                Registration & Specs
              </h3>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                  <label className={fieldLabelClass}>
                    Plate State / Province
                  </label>
                  <Input
                    index={4}
                    name="plate_state_province"
                    value={formData.plate_state_province || ""}
                    className={fieldInputClass}
                    placeHolder="Plate State"
                    onChange={updateFormData}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Plate Number
                  </label>
                  <Input
                    index={5}
                    name="plate"
                    value={formData.plate || ""}
                    className={fieldInputClass}
                    placeHolder="Plate"
                    onChange={updateFormData}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Color
                  </label>
                  <Input
                    index={6}
                    name="color"
                    value={formData.color || ""}
                    className={fieldInputClass}
                    placeHolder="Color"
                    onChange={updateFormData}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Engine
                  </label>
                  <Input
                    index={7}
                    name="engine"
                    value={formData.engine || ""}
                    className={fieldInputClass}
                    placeHolder="Engine"
                    onChange={updateFormData}
                  />
                </div>
              </div>

              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                  <label className={fieldLabelClass}>
                    Front Tire Size
                  </label>
                  <Input
                    index={8}
                    name="tire_size_front"
                    value={formData.tire_size_front || ""}
                    className={fieldInputClass}
                    placeHolder="Front Tire Size"
                    onChange={updateFormData}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Rear Tire Size
                  </label>
                  <Input
                    index={9}
                    name="tire_size_rear"
                    value={formData.tire_size_rear || ""}
                    className={fieldInputClass}
                    placeHolder="Rear Tire Size"
                    onChange={updateFormData}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Vehicle Number
                  </label>
                  <Input
                    index={10}
                    name="vehicle_number"
                    value={formData.vehicle_number || ""}
                    className={fieldInputClass}
                    placeHolder="Vehicle Number"
                    onChange={updateFormData}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Trim
                  </label>
                  <Input
                    index={11}
                    name="trim"
                    value={formData.trim || ""}
                    className={fieldInputClass}
                    placeHolder="Trim"
                    onChange={updateFormData}
                  />
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-500">
                Usage Details
              </h3>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                  <label className={fieldLabelClass}>
                    Fuel Type
                  </label>
                  <Select
                    index={16}
                    name="fuel_type"
                    required={false}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    value={formData.fuel_type}
                    className={fieldInputClass}
                    placeHolder="Fuel Type"
                    onChange={updateFormData}
                    options={fuelTypeOptions}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Drivetrain
                  </label>
                  <Select
                    index={13}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    required={false}
                    name="drivetrain"
                    value={formData.drivetrain}
                    className={fieldInputClass}
                    placeHolder="Drivetrain"
                    onChange={updateFormData}
                    options={drivetrainOptions}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Odometer Unit
                  </label>
                  <Select
                    index={14}
                    inputErrors={inputErrors}
                    setInputErrors={setInputErrors}
                    missingFields={missingFields}
                    name="odo_unit"
                    required={false}
                    value={formData.odo_unit}
                    className={fieldInputClass}
                    placeHolder="Odo Unit"
                    onChange={updateFormData}
                    options={odoUnitOptions}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Current Odometer
                  </label>
                  <Input
                    index={15}
                    name="current_odometer"
                    type="number"
                    value={formData.current_odometer || ""}
                    className={fieldInputClass}
                    placeHolder="Current Odometer"
                    onChange={updateFormData}
                  />
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-500">
                Additional Information
              </h3>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div>
                  <label className={fieldLabelClass}>
                    Notes
                  </label>
                  <Input
                    index={20}
                    name="notes"
                    value={formData.notes || ""}
                    className={fieldInputClass}
                    placeHolder="Notes"
                    onChange={updateFormData}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Group
                  </label>
                  <Input
                    index={21}
                    name="group"
                    value={formData.group || ""}
                    className={fieldInputClass}
                    placeHolder="Group"
                    onChange={updateFormData}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Acquisition Cost
                  </label>
                  <Input
                    index={22}
                    name="acquisition_cost"
                    type="number"
                    value={formData.acquisition_cost || ""}
                    className={fieldInputClass}
                    placeHolder="Acquisition Cost"
                    onChange={updateFormData}
                  />
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Location
                  </label>
                  <Input
                    index={23}
                    name="location"
                    value={formData.location || ""}
                    className={fieldInputClass}
                    placeHolder="Location"
                    onChange={updateFormData}
                  />
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-emerald-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          {showInputError && (
            <p className="text-sm font-semibold text-red-500">
              Please fill all the fields as directed.
            </p>
          )}
          <div className="ml-auto flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-full border border-emerald-200 px-6 py-2 text-sm font-semibold text-emerald-600 transition-colors hover:bg-emerald-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => (isEditMode ? updateFleetCar() : addFleetCar())}
              className="rounded-full bg-emerald-600 px-6 py-2 text-sm font-semibold text-white shadow-[0_10px_25px_rgba(16,185,129,0.3)] transition-colors hover:bg-emerald-700"
            >
              {isEditMode ? "Save Changes" : "Add Vehicle"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddFleetCar;
